import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { lang } from "../../../utils/langs";

// Initialize pdfMake vfs
pdfMake.vfs = pdfFonts.vfs;

/**
 * PdfBuilder
 * Utility to abstract the creation of PDF reports using pdfMake.
 */
export class PdfBuilder {
    constructor(metadata) {
        this.metadata = metadata;
        this.content = [];
        this.pageOrientation = "portrait";
        this.pageSize = "letter";
        this.defaultDecimals = 0;
    }

    /**
     * Sets the orientation of the PDF (portrait or landscape).
     */
    setOrientation(orientation) {
        this.pageOrientation = orientation;
    }

    /**
     * Formats currency based on standardized rules.
     */
    formatCurrency(val, decimals = null) {
        const d = decimals !== null ? decimals : this.defaultDecimals;
        const num = typeof val === 'number' ? val : parseFloat(val || 0);
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: d,
            maximumFractionDigits: d
        }).format(isNaN(num) ? 0 : num);
    }

    /**
     * Formats any other cell value, limiting numbers and percentages to max 2 decimals.
     */
    formatGeneralValue(val) {
        if (typeof val === 'number') {
            if (Number.isInteger(val)) return String(val);
            return String(Math.round(val * 100) / 100);
        }
        if (typeof val === 'string') {
            if (val.endsWith('%')) {
                const numPart = val.slice(0, -1);
                if (/^-?\d+(\.\d+)?$/.test(numPart)) {
                    const num = parseFloat(numPart);
                    if (!isNaN(num)) {
                        return `${Math.round(num * 100) / 100}%`;
                    }
                }
            }
            if (/^-?\d+\.\d+$/.test(val)) {
                const num = parseFloat(val);
                if (!isNaN(num)) {
                    return String(Math.round(num * 100) / 100);
                }
            }
        }
        return String(val ?? "");
    }

    /**
     * Adds the standardized header for accounting reports.
     */
    addMainHeader() {
        this.content.push(
            { text: lang("es", this.metadata.real_type || this.metadata.report_type), style: "header" },
            { text: `Empresa: ${this.metadata.company_name || ""}`, style: "subheader" },
            { text: `Periodo: ${this.metadata.period_from} → ${this.metadata.period_to}`, style: "subheader" },
            { text: `Generado en: ${this.metadata.generated_at}`, style: "subheader" },
            { text: "\n" }
        );
    }

    /**
     * Adds a section title.
     */
    addSectionHeader(title) {
        this.content.push({ text: lang("es", title), style: "sectionHeader" });
    }

    /**
     * Adds a text element.
     */
    addText(text, style = null) {
        this.content.push({ text, style });
    }

    /**
     * Formats a totals row for a PDF table.
     */
    getTotalsRow(values, currencyIndices = []) {
        return values.map((val, i) => {
            const isCurrency = currencyIndices.includes(i);
            return {
                text: isCurrency && typeof val === 'number' ? this.formatCurrency(val) : String(val || ""),
                alignment: isCurrency ? "right" : "left",
                bold: true,
                noWrap: isCurrency,
                fillColor: "#F2F2F2"
            };
        });
    }

    /**
     * Adds a table to the PDF.
     */
    addTable(headers, bodyRows, options = {}) {
        const tableBody = [
            headers.map((h) => ({ text: h, style: "tableHeader" })),
            ...bodyRows
        ];

        this.content.push({
            table: {
                headerRows: 1,
                widths: options.widths || headers.map(() => "auto"),
                body: tableBody,
            },
            layout: options.layout || "lightHorizontalLines",
            margin: [0, 0, 0, 10],
        });
    }

    /**
     * Finalizes the PDF and triggers download.
     */
    download(download = true) {
        const docDefinition = {
            pageSize: this.pageSize,
            pageOrientation: this.pageOrientation,
            content: this.content,
            styles: {
                header: { fontSize: 16, bold: true, alignment: "center", margin: [0, 0, 0, 10] },
                subheader: { fontSize: 10, margin: [0, 2, 0, 2] },
                sectionHeader: { fontSize: 12, bold: true, color: "#1F4E78", margin: [0, 10, 0, 5] },
                tableHeader: { bold: true, fontSize: 10, color: "white", fillColor: "#4472C4", alignment: "center" },
                balanceInfo: { fontSize: 10, bold: true, color: "#70AD47", margin: [0, 0, 0, 5] },
                empty: { italics: true, fontSize: 9, color: "gray" }
            },
            defaultStyle: { fontSize: 9 }
        };

        const pdfDoc = pdfMake.createPdf(docDefinition);
        if (download) {
            const fileName = `${this.metadata.report_type}_${this.metadata.company_name}.pdf`;
            pdfDoc.download(fileName);
        }
        return pdfDoc;
    }
}
